import React, {ReactNode, useEffect, useState} from "react";
import {
	Box,
	Button,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableFooter,
	TableHead,
	TableRow,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import {
	AccountBalance,
	EventBusy,
	InfoOutlined,
	Savings,
	TrendingDown,
	TrendingUp,
	TuneRounded,
	WarningAmberRounded,
} from "@mui/icons-material";
import {
	AccountingForecast,
	AccountingForecastRestControllerApiFactory,
	AccountingForecastSettings,
	PnlRow,
	TreasuryRow,
} from "arteco-api-client-ts";
import {useApiManager} from "../../../../components/utils/api";
import {btnSx, EmptyState, Page, PageHeader, Panel, SkeletonRows, SoftChip, StatGrid, StatTile, tableContainerSx, tableSx} from "../../../../components/kit";
import {eur0} from "../../billing/overview/kpis";
import {ForecastSettingsDialog} from "./settings_dialog";

const MONTHS = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const num = {fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap"} as const;

/** «7,4» meses. */
export const months1 = (n?: number | null) => n === undefined || n === null ? "—"
	: n.toLocaleString("es-ES", {minimumFractionDigits: 1, maximumFractionDigits: 1});

/** Importe con signo y color: verde lo que suma, rojo lo que resta, gris el cero o lo que no se sabe. */
const Amount = ({v, bold, color = true}: { v?: number | null, bold?: boolean, color?: boolean }) => (
	<Typography component="span" variant="body2" fontWeight={bold ? 700 : 400}
				sx={{...num, color: v === undefined || v === null ? "text.disabled" : !color || Math.round(v) === 0 ? "text.primary" : v < 0 ? "error.main" : "text.primary"}}>
		{v === undefined || v === null ? "sin dato" : eur0(v)}
	</Typography>
);

const Note = ({children}: { children: ReactNode }) => (
	<Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{mt: 0.5}}>
		<InfoOutlined sx={{fontSize: 14, mt: "2px", color: "text.disabled"}}/>
		<Typography variant="caption" color="text.secondary">{children}</Typography>
	</Stack>
);

/**
 * Previsión (#14560): cómo cerrará el año la cuenta de resultados, la tesorería de los próximos seis meses y el colchón
 * por encima del cual hay dinero para repartir o invertir. Cada cifra prevista dice de dónde sale.
 */
export const AccountingForecastPage = () => {
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(AccountingForecastRestControllerApiFactory);
	const [data, setData] = useState<AccountingForecast | undefined>();
	const [failed, setFailed] = useState(false);
	const [settingsOpen, setSettingsOpen] = useState(false);
	const [trigger, setTrigger] = useState(0);

	useEffect(() => {
		setData(undefined);
		setFailed(false);
		apiManager.execute(ctrl.forecast()).then(setData).catch(() => setFailed(true));
	}, [trigger]);

	const saveSettings = (s: AccountingForecastSettings) =>
		apiManager.execute(ctrl.saveForecastSettings(s)).then(() => setTrigger(t => t + 1));

	const through = data && data.bookedThroughMonth ? `contabilidad hasta ${MONTHS[data.bookedThroughMonth - 1]}` : "sin nóminas en el diario";

	return (
		<Page>
			<PageHeader title="Previsión"
						subtitle={data ? `Cierre de ${data.year} y tesorería de los próximos seis meses · ${through}` : "Cierre del año y tesorería de los próximos seis meses"}
						actions={<Button variant="outlined" startIcon={<TuneRounded/>} sx={btnSx} disabled={!data}
										 onClick={() => setSettingsOpen(true)}>Parámetros</Button>}/>
			{failed
				? <Panel><EmptyState icon={<EventBusy/>} title="No se ha podido calcular la previsión"
									 description="Vuelve a intentarlo en un momento; si sigue igual, avisa con el botón de abajo a la derecha."
									 action={<Button variant="outlined" sx={btnSx} onClick={() => setTrigger(t => t + 1)}>Reintentar</Button>}/></Panel>
				: !data
					? <SkeletonRows rows={5} height={110}/>
					: <>
						{/* Fuera del Stack: un Grid con spacing dentro de un Stack desborda en móvil. */}
						<Headline f={data}/>
						<Stack spacing={3}>
						{(data.warnings?.length ?? 0) > 0 && <Warnings items={data.warnings!}/>}
						<ProfitLoss f={data}/>
						<TreasuryPanel f={data}/>
						<CushionPanel f={data}/>
						<Box/>
					</Stack>
					</>}
			{data?.settings &&
				<ForecastSettingsDialog open={settingsOpen} value={data.settings} onClose={() => setSettingsOpen(false)} onSave={saveSettings}/>}
		</Page>
	);
};

const Headline = ({f}: { f: AccountingForecast }) => {
	const r = f.result!;
	const c = f.cushion!;
	const close = r.closeBeforeTax ?? 0;
	return (
		<StatGrid>
			<StatTile label="Cierre previsto antes de impuestos" value={eur0(close)} tone={close >= 0 ? "success" : "error"}
					  icon={close >= 0 ? <TrendingUp fontSize="small"/> : <TrendingDown fontSize="small"/>}
					  hint={<Typography variant="caption" color="text.secondary">Hoy, en el diario: {eur0(r.bookedBeforeTax)}</Typography>}/>
			<StatTile label="Después de impuestos" value={r.closeAfterTax === undefined || r.closeAfterTax === null ? "—" : eur0(r.closeAfterTax)}
					  tone={r.closeAfterTax === undefined || r.closeAfterTax === null ? "neutral" : r.closeAfterTax >= 0 ? "success" : "error"}
					  icon={<AccountBalance fontSize="small"/>}
					  hint={<Typography variant="caption" color="text.secondary">
						  {r.corporateTax === undefined || r.corporateTax === null ? "Sin tipo del impuesto en los parámetros" : `Impuesto previsto ${eur0(r.corporateTax)}`}
					  </Typography>}/>
			<StatTile label="De gestión (rápel en su año)" value={eur0(r.managementBeforeTax)} tone="info"
					  icon={<InfoOutlined fontSize="small"/>}
					  hint={<Typography variant="caption" color="text.secondary">Antes de impuestos, como la visión general</Typography>}/>
			<StatTile label="Colchón de tesorería" value={c.months === undefined || c.months === null ? "—" : `${months1(c.months)} meses`}
					  tone={c.months === undefined || c.months === null ? "neutral" : c.belowTarget ? "error" : "success"}
					  icon={<Savings fontSize="small"/>}
					  hint={<Typography variant="caption" color="text.secondary">Objetivo {months1(c.targetMonths)} meses</Typography>}/>
			<StatTile label="Por encima del colchón" value={eur0(c.aboveTarget)}
					  tone={c.aboveTarget === undefined || c.aboveTarget === null ? "neutral" : c.aboveTarget >= 0 ? "success" : "error"}
					  icon={<AccountBalance fontSize="small"/>}
					  hint={<Typography variant="caption" color="text.secondary">Repartible o invertible sin tocarlo</Typography>}/>
		</StatGrid>
	);
};

const Warnings = ({items}: { items: string[] }) => (
	<Panel title="Antes de fiarse de la cifra" icon={<WarningAmberRounded fontSize="small" sx={{color: "warning.main"}}/>}
		   subtitle="Datos que faltan o supuestos que conviene ver.">
		<Stack spacing={1}>
			{items.map((w, i) => (
				<Stack key={i} direction="row" spacing={1} alignItems="flex-start">
					<Box sx={{width: 6, height: 6, borderRadius: "50%", bgcolor: "warning.main", mt: "7px", flexShrink: 0}}/>
					<Typography variant="body2" color="text.secondary">{w}</Typography>
				</Stack>
			))}
		</Stack>
	</Panel>
);

// ── Cuenta de resultados ─────────────────────────────────────────────────────────────────────

const ProfitLoss = ({f}: { f: AccountingForecast }) => {
	const theme = useTheme();
	const compact = useMediaQuery(theme.breakpoints.down("md"));
	const r = f.result!;
	const rows = f.pnl || [];
	const tax = rows.find(x => x.key === "corporateTax");
	const operating = rows.filter(x => x.key !== "corporateTax");
	const prevYear = f.year! - 1;
	const totals: PnlRow[] = [
		{key: "bt", label: "Resultado antes de impuestos", booked: r.bookedBeforeTax, pending: r.pendingBeforeTax, close: r.closeBeforeTax, previousYear: r.previousYearBeforeTax},
		...(tax ? [tax] : []),
		{
			key: "at", label: "Resultado después de impuestos", booked: (r.bookedBeforeTax ?? 0) + (tax?.booked ?? 0),
			pending: r.closeAfterTax === undefined || r.closeAfterTax === null ? undefined : r.closeAfterTax - ((r.bookedBeforeTax ?? 0) + (tax?.booked ?? 0)),
			close: r.closeAfterTax,
			previousYear: r.previousYearBeforeTax === undefined || r.previousYearBeforeTax === null ? undefined : r.previousYearBeforeTax + (tax?.previousYear ?? 0),
		},
	];

	return (
		<Panel title={`Cuenta de resultados de ${f.year}`}
			   subtitle="Lo contabilizado es el diario; lo pendiente, lo que falta por devengar hasta el 31 de diciembre. Ingresos en positivo y gastos en negativo.">
			{compact
				? <Stack spacing={1.5}>
					{[...operating, ...totals].map(row => <PnlCard key={row.key} row={row} prevYear={prevYear}
																	strong={row.key === "bt" || row.key === "at"}/>)}
				</Stack>
				: <TableContainer sx={tableContainerSx}>
					<Table size="small" sx={tableSx}>
						<TableHead>
							<TableRow>
								<TableCell sx={{width: "46%"}}>Concepto</TableCell>
								<TableCell align="right">Contabilizado</TableCell>
								<TableCell align="right">Pendiente</TableCell>
								<TableCell align="right">Cierre previsto</TableCell>
								<TableCell align="right">{prevYear}</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{operating.map(row => <PnlLine key={row.key} row={row}/>)}
						</TableBody>
						<TableFooter>
							{totals.map(row => <PnlLine key={row.key} row={row} strong={row.key !== "corporateTax"}/>)}
						</TableFooter>
					</Table>
				</TableContainer>}
			<Box sx={{mt: 2}}>
				<Note>
					Con el rápel de cada año en su año, como la visión general, el cierre antes de impuestos sería <strong>{eur0(r.managementBeforeTax)}</strong>
					{(r.priorYearRebate ?? 0) > 0 && <> (sin los {eur0(r.priorYearRebate)} de notas de abono del rápel de años anteriores que se contabilizaron este año)</>}.
				</Note>
			</Box>
		</Panel>
	);
};

const PnlLine = ({row, strong}: { row: PnlRow, strong?: boolean }) => (
	<TableRow>
		<TableCell>
			<Typography variant="body2" fontWeight={strong ? 700 : 600}>{row.label}</Typography>
			{row.accounts && <Typography variant="caption" color="text.disabled">{row.accounts}</Typography>}
			{row.assumption && <Note>{row.assumption}</Note>}
		</TableCell>
		<TableCell align="right" sx={{verticalAlign: "top"}}><Amount v={row.booked} bold={strong}/></TableCell>
		<TableCell align="right" sx={{verticalAlign: "top"}}><Amount v={row.pending} bold={strong}/></TableCell>
		<TableCell align="right" sx={{verticalAlign: "top"}}><Amount v={row.close} bold/></TableCell>
		<TableCell align="right" sx={{verticalAlign: "top", color: "text.secondary"}}>
			{row.previousYear === undefined || row.previousYear === null
				? <Typography variant="body2" color="text.disabled">—</Typography>
				: <Amount v={row.previousYear} bold={strong} color={false}/>}
		</TableCell>
	</TableRow>
);

const PnlCard = ({row, prevYear, strong}: { row: PnlRow, prevYear: number, strong?: boolean }) => (
	<Box sx={{border: "1px solid", borderColor: "divider", borderRadius: 2.5, p: 1.5, bgcolor: strong ? "grey.50" : "background.paper"}}>
		<Stack direction="row" justifyContent="space-between" spacing={1} alignItems="baseline">
			<Typography variant="body2" fontWeight={700}>{row.label}</Typography>
			<Amount v={row.close} bold/>
		</Stack>
		<Box sx={{display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)", gap: 1, mt: 1}}>
			{([["Contabilizado", row.booked], ["Pendiente", row.pending], [String(prevYear), row.previousYear]] as [string, number | undefined][]).map(([l, v]) => (
				<Box key={l} sx={{minWidth: 0}}>
					<Typography variant="caption" color="text.secondary" component="div">{l}</Typography>
					{l === String(prevYear) && (v === undefined || v === null)
						? <Typography variant="body2" color="text.disabled">—</Typography>
						: <Amount v={v}/>}
				</Box>
			))}
		</Box>
		{row.assumption && <Note>{row.assumption}</Note>}
	</Box>
);

// ── Tesorería ────────────────────────────────────────────────────────────────────────────────

const TreasuryPanel = ({f}: { f: AccountingForecast }) => {
	const t = f.treasury!;
	const months = t.months || [];
	const rows = t.rows || [];
	const ins = rows.filter(r => r.kind === "IN");
	const outs = rows.filter(r => r.kind === "OUT");
	const line = (r: TreasuryRow, sign: 1 | -1) => (
		<TableRow key={r.key}>
			<TableCell sx={{position: "sticky", left: 0, bgcolor: "background.paper", zIndex: 1, minWidth: 170}}>
				<Typography variant="body2">{r.label}</Typography>
			</TableCell>
			{(r.amounts || []).map((a, i) => (
				<TableCell key={i} align="right">
					{a === undefined || a === null
						? <Typography variant="body2" sx={{color: "warning.main", ...num}}>sin dato</Typography>
						: <Typography variant="body2" sx={{...num, color: Math.round(a) === 0 ? "text.disabled" : sign < 0 ? "error.main" : "success.main"}}>
							{Math.round(a) === 0 ? "—" : (sign < 0 ? "−" : "+") + eur0(a)}
						</Typography>}
				</TableCell>
			))}
		</TableRow>
	);
	const balanceRow = (label: string, pick: (i: number) => number | undefined) => (
		<TableRow>
			<TableCell sx={{position: "sticky", left: 0, bgcolor: "grey.50", zIndex: 1}}>
				<Typography variant="body2" fontWeight={700}>{label}</Typography>
			</TableCell>
			{months.map((m, i) => (
				<TableCell key={m.month} align="right" sx={{bgcolor: "grey.50"}}>
					<Typography variant="body2" fontWeight={700} sx={{...num, color: (pick(i) ?? 0) < 0 ? "error.main" : "text.primary"}}>{eur0(pick(i))}</Typography>
				</TableCell>
			))}
		</TableRow>
	);

	return (
		<Panel title="Tesorería a seis meses"
			   subtitle={`Del saldo de bancos de hoy (${eur0(t.opening)}, cuentas 57) a final de ${months[months.length - 1]?.label ?? ""}. Lo vencido y lo que queda de este mes, en el primero.`}
			   action={<SoftChip tone={(t.minBalance ?? 0) < 0 ? "error" : "neutral"} label={`Saldo mínimo ${eur0(t.minBalance)} en ${t.minBalanceMonth}${months.some(m => m.label === t.minBalanceMonth && m.incomplete) ? " *" : ""}`}/>}>
			<TableContainer sx={{...tableContainerSx, overflowX: "auto"}}>
				<Table size="small" sx={{...tableSx, minWidth: 820}}>
					<TableHead>
						<TableRow>
							<TableCell sx={{position: "sticky", left: 0, zIndex: 2}}>Concepto</TableCell>
							{months.map(m => (
								<TableCell key={m.month} align="right">
									{m.label}{m.incomplete && <Typography component="span" sx={{color: "warning.main"}}> *</Typography>}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{balanceRow("Saldo inicial", i => months[i].opening)}
						{ins.map(r => line(r, 1))}
						{outs.map(r => line(r, -1))}
						{balanceRow("Saldo final", i => months[i].closing)}
					</TableBody>
				</Table>
			</TableContainer>
			{months.some(m => m.incomplete) &&
				<Note><Box component="span" sx={{color: "warning.main"}}>*</Box> Mes con algún pago que vence sin importe conocido: el saldo no lo descuenta.</Note>}
			<Box sx={{mt: 2, display: "grid", gap: 0.5}}>
				{rows.map(r => <Note key={r.key}><strong>{r.label}.</strong> {r.assumption}</Note>)}
			</Box>
		</Panel>
	);
};

// ── Colchón ──────────────────────────────────────────────────────────────────────────────────

const CushionPanel = ({f}: { f: AccountingForecast }) => {
	const c = f.cushion!;
	const lines: [string, number | undefined | null, string?][] = [
		["Bancos (57)", c.bank],
		["Hacienda y Seguridad Social pendientes", c.taxDebts === undefined ? undefined : -c.taxDebts, "475, 476 y 477 menos 472"],
		["Liquidez neta", c.netLiquidity],
		[`Colchón objetivo: ${months1(c.targetMonths)} meses de ${eur0(c.fixedMonthly)}`, c.targetAmount === undefined ? undefined : -c.targetAmount,
			`Nóminas ${eur0(c.payrollMonthly)} y gastos ${eur0(c.expensesMonthly)} al mes`],
		...(c.corporateTax === undefined || c.corporateTax === null ? [] : [["Impuesto de Sociedades previsto", -c.corporateTax, "Se paga en julio"] as [string, number, string]]),
	];
	return (
		<Panel title="Colchón"
			   subtitle="Lo que hay por encima del colchón se puede repartir o invertir sin tocarlo. Lo legal del dividendo (reservas y pérdidas anteriores) se mira con el contable."
			   action={c.months === undefined || c.months === null
				   ? <SoftChip tone="neutral" label="Sin costes fijos medidos"/>
				   : <SoftChip tone={c.belowTarget ? "error" : "success"} label={`${months1(c.months)} meses${c.belowTarget ? ", por debajo del objetivo" : ""}`}/>}>
			<Box sx={{display: "grid", gap: 1, maxWidth: 620}}>
				{lines.map(([label, v, hint]) => (
					<Stack key={label} direction="row" justifyContent="space-between" spacing={2} alignItems="baseline">
						<Box sx={{minWidth: 0}}>
							<Typography variant="body2" fontWeight={label === "Liquidez neta" ? 700 : 400}>{label}</Typography>
							{hint && <Typography variant="caption" color="text.disabled">{hint}</Typography>}
						</Box>
						<Amount v={v} bold={label === "Liquidez neta"}/>
					</Stack>
				))}
				<Box sx={{borderTop: "1px solid", borderColor: "divider", pt: 1}}>
					<Stack direction="row" justifyContent="space-between" spacing={2} alignItems="baseline">
						<Typography variant="body2" fontWeight={700}>Por encima del colchón</Typography>
						<Typography variant="h6" fontWeight={700}
									sx={{...num, color: c.aboveTarget === undefined || c.aboveTarget === null ? "text.disabled" : c.aboveTarget < 0 ? "error.main" : "success.main"}}>
							{c.aboveTarget === undefined || c.aboveTarget === null ? "sin dato" : eur0(c.aboveTarget)}
						</Typography>
					</Stack>
				</Box>
			</Box>
			{c.assumption && <Box sx={{mt: 1.5}}><Note>{c.assumption}</Note></Box>}
		</Panel>
	);
};
